import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { BookOpen, Trash2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  useDiaryReadings, 
  useDiaryMutations, 
  usePrefetchDiary
} from '../hooks/useDiary';
import diaryService from '../services/diaryService';
import authService from '../services/authService';
import aiService from '../services/aiService';
import { analytics } from '../services/analytics';
import logger from '../utils/logger';

const DiaryPage = () => {
  const { isAuthenticated } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [generatingAI, setGeneratingAI] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // React Query hooks for data fetching
  const { 
    data: readingsData, 
    isLoading: readingsLoading, 
    error: readingsError,
    isFetching: readingsFetching
  } = useDiaryReadings(currentPage, 20, { spreadType: filterType });

  // Mutations for data modifications
  const { deleteReading: deleteReadingMutation, updateReading } = useDiaryMutations();
  const { prefetchReadings } = usePrefetchDiary();

  // Extract readings and pagination from data
  const readings = readingsData?.readings || [];
  const totalPages = readingsData?.totalPages || 1;
  const loading = readingsLoading;
  const error = readingsError?.message || '';

  // Debug logging
  logger.debug('DiaryPage state:', {
    filterType,
    readings: readings.length,
    readingsData,
    currentPage
  });

  // Log the first few readings to see their structure
  if (readings.length > 0) {
    logger.debug('Sample readings structure:', readings.slice(0, 3).map(reading => ({
      spread: reading.spread,
      spreadName: reading.spreadName,
      metadata: reading.metadata,
      timestamp: reading.timestamp
    })));
  }

  // Load user stats
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const statsData = await authService.getUserStats();
      setStats(statsData.stats || null);
    } catch (error) {
      logger.error('Failed to load stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Track diary view
      analytics.trackViewDiary();
      
      // Load stats only once when authenticated
      loadStats();
    }
  }, [isAuthenticated, loadStats]);

  useEffect(() => {
    if (isAuthenticated) {
      // Prefetch next page for better UX
      if (currentPage < totalPages) {
        prefetchReadings(currentPage + 1, 20, { spreadType: filterType });
      }
    }
  }, [isAuthenticated, currentPage, totalPages, filterType, prefetchReadings]);

  // Handle reading deletion with React Query
  const handleDeleteReading = async (id) => {
    if (!window.confirm(t('diary.deleteConfirm'))) {
      return;
    }

    try {
      await deleteReadingMutation.mutateAsync(id);
      
      // Track deletion
      analytics.trackDeleteReading(id);
    } catch (err) {
      logger.error('Error deleting reading:', err);
      // Error handling is done in the mutation
    }
  };

  // Handle AI explanation generation
  const handleGenerateAI = async (reading) => {
    const readingId = reading._id || reading.id;
    if (generatingAI.has(readingId)) return;
    
    setGeneratingAI(prev => new Set([...prev, readingId]));
    
    try {
      // Format the reading data for AI processing
      const spreadData = aiService.formatSpreadForAI({
        spreadName: reading.spread || 'Custom Reading',
        spreadType: reading.spread || 'custom',
        cards: reading.cards.map(cardData => ({
          name: cardData.card?.name || cardData.name,
          meaning: cardData.card?.meaning || cardData.meaning,
          position: cardData.card?.position || cardData.position || 'upright',
          image: cardData.card?.image || cardData.image,
          link: cardData.card?.link || cardData.link
        })),
        question: reading.question || '',
        cardCount: reading.cards.length,
        spreadInterpretations: reading.cards.map(cardData => cardData.position)
      });

      // Generate AI explanation with current language
      const response = await aiService.generateSpreadExplanation(spreadData, currentLanguage);
      
      // Update the reading with AI explanation
      await updateReading.mutateAsync({ 
        id: readingId, 
        data: { 
          aiExplanation: response.explanation 
        } 
      });
      
    } catch (error) {
      logger.error('Error generating AI explanation:', error);
      alert(`Failed to generate AI explanation: ${error.message}`);
    } finally {
      setGeneratingAI(prev => {
        const newSet = new Set(prev);
        newSet.delete(readingId);
        return newSet;
      });
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilter) => {
    logger.debug('Filter changed to:', newFilter);
    setFilterType(newFilter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle page changes
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  if (!isAuthenticated) {
    return (
      <div className="diary-auth-required">
        <div className="auth-message">
          <BookOpen size={48} />
          <h2>{t('diary.signInRequired')}</h2>
          <p>{t('diary.signInMessage')}</p>
          <Link to="/login" className="btn btn-primary">
            {t('diary.signIn')}
          </Link>
        </div>
      </div>
    );
  }

  if (loading && readings.length === 0) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>{t('diary.loading')}</p>
      </div>
    );
  }

  return (
    <div className="diary-page">
      <div className="page-header">
        <h1>{t('diary.title')}</h1>
        <p>{t('diary.subtitle')}</p>
      </div>

      {/* Stats Section */}
      <div className="stats-section">
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon">🔮</div>
            <div className="stat-content">
              <h3>{statsLoading ? '...' : (stats?.totalReadings || 0)}</h3>
              <p>{t('diary.totalReadings')}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">❤️</div>
            <div className="stat-content">
              <h3>{statsLoading ? '...' : (stats?.favoriteSpread || t('diary.filterLove'))}</h3>
              <p>{t('diary.favoriteSpread')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="diary-controls">
        <div className="filter-controls">
          <select 
            value={filterType} 
            onChange={(e) => handleFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('diary.filterAll')}</option>
            <option value="love">{t('diary.filterLove')}</option>
            <option value="career">{t('diary.filterCareer')}</option>
            <option value="personal">{t('diary.filterPersonal')}</option>
            <option value="mental">{t('diary.filterMental')}</option>
            <option value="single">{t('diary.filterSingle')}</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error">
          {error}
        </div>
      )}

      {readings.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <BookOpen size={64} style={{ color: '#ccc', marginBottom: '1rem' }} />
            <h3>{t('diary.noReadings')}</h3>
            <p>{t('diary.emptyMessage')}</p>
            <Link to="/import" className="btn btn-primary">
              {t('diary.importReading')}
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="readings-grid">
            {readings.map((reading) => (
              <ReadingCard 
                key={reading._id || reading.id} 
                reading={reading} 
                onDelete={handleDeleteReading}
                onGenerateAI={handleGenerateAI}
                isGeneratingAI={generatingAI.has(reading._id || reading.id)}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                {t('diary.previous')}
              </button>
              
              <span className="pagination-info">
                {t('diary.page')} {currentPage} {t('diary.of')} {totalPages}
              </span>
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                {t('diary.next')}
              </button>
            </div>
          )}

          {/* Loading indicator for background fetching */}
          {readingsFetching && readings.length > 0 && (
            <div className="background-loading">
              {t('diary.refreshing')}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ReadingCard = ({ reading, onDelete, onGenerateAI, isGeneratingAI }) => {
  const { t } = useLanguage();
  logger.debug('ReadingCard received:', reading);
  logger.debug('Reading cards structure:', reading.cards);
  
  // Define spread type colors (similar to relationship types)
  const getSpreadColor = (spreadType) => {
    const colors = {
      'love': 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
      'career': 'linear-gradient(135deg, #4834d4 0%, #686de0 100%)', 
      'personal': 'linear-gradient(135deg, #00d2d3 0%, #54a0ff 100%)',
      'celtic-cross': 'linear-gradient(135deg, #5f27cd 0%, #341f97 100%)',
      'three-card': 'linear-gradient(135deg, #00d2d3 0%, #01a3a4 100%)',
      'mental-healing': 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
      'personal-growth': 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
      'default': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    };
    return colors[spreadType?.toLowerCase()] || colors.default;
  };

  // Define spread type icons (following the type-icon pattern from spread pages)
  const getSpreadIcon = (reading) => {
    const spreadName = (reading.spread || reading.spreadName || '').toLowerCase();
    const metadata = reading.metadata || {};
    
    // Handle Personal Growth spread subtypes
    if (metadata.spreadConfig?.growthType || spreadName.includes('growth') || spreadName.includes('personal') || spreadName.includes('development') || spreadName.includes('journey')) {
      const growthType = metadata.spreadConfig?.growthType;
      
      switch (growthType) {
        case 'life-journey':
          return 'icon_08_enh_v2.png';
        case 'hero-journey':
          return 'icon_03_family_v2.png';
        case 'spiritual-growth':
          return 'icon_01_find_v2.png';
        case 'transformation':
          return 'icon_04_biz_v2.png';
        case 'self-reflection':
          return 'icon_05_col_v2.png';
        case 'self-improvement':
          return 'icon_10_rej_v2.png';
        case 'self-defeating':
          return 'icon_07_sh_v2.png';
        default:
          return 'icon_01_find_v2.png';
      }
    }
    
    // Handle Mental Healing spread subtypes
    if (metadata.spreadConfig?.healingType || spreadName.includes('healing') || spreadName.includes('mental') || spreadName.includes('therapy') || spreadName.includes('wellness')) {
      const healingType = metadata.spreadConfig?.healingType;
      
      switch (healingType) {
        case 'self-healing':
          return 'icon_07_sh_v2.png';
        case 'energy-healing':
          return 'icon_08_enh_v2.png';
        case 'soulmate-healing':
          return 'icon_05_col_v2.png';
        case 'pregnancy':
          return 'icon_03_family_v2.png';
        case 'physical-healing':
          return 'icon_09_phy_v2.png';
        case 'rejection-healing':
          return 'icon_10_rej_v2.png';
        default:
          return 'icon_02_love_v2.png';
      }
    }
    
    // Handle Career spread subtypes
    if (metadata.spreadConfig?.careerType || spreadName.includes('career') || spreadName.includes('job') || spreadName.includes('work') || spreadName.includes('professional') || spreadName.includes('business')) {
      const careerType = metadata.spreadConfig?.careerType;
      
      switch (careerType) {
        case 'career-path':
          return 'icon_03_family_v2.png';
        case 'workplace-collaboration':
          return 'icon_04_biz_v2.png';
        case 'professional-growth':
          return 'icon_01_find_v2.png';
        case 'purpose-discovery':
          return 'icon_02_love_v2.png';
        default:
          return 'icon_04_biz_v2.png';
      }
    }
    
    // Handle Love spread
    if (spreadName.includes('love') || spreadName.includes('relationship') || spreadName.includes('couple') || spreadName.includes('romance')) {
      return 'icon_02_love_v2.png';
    }
    
    // Handle general types
    if (spreadName.includes('family') || spreadName.includes('celtic') || spreadName.includes('cross')) {
      return 'icon_03_family_v2.png';
    }
    if (spreadName.includes('three') || spreadName.includes('daily') || spreadName.includes('day')) {
      return 'icon_01_find_v2.png';
    }
    
    // Default fallback
    return 'icon_01_find_v2.png';
  };

  const spreadType = reading.spread || reading.spreadName || 'personal';
  const cardColor = getSpreadColor(spreadType);
  const iconSrc = getSpreadIcon(reading);
  
  return (
    <Link 
      to={`/reading/${reading._id || reading.id}`}
      className="reading-card-link"
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div 
        className="reading-card diary-reading-card"
        style={{ '--card-color': cardColor }}
      >
      <div className="reading-card-body">
        {/* Left Column - 60% width */}
        <div className="reading-content-left">
          <div className="reading-card-header">
            <div className="type-icon">
              <img 
                src={`/images/${iconSrc}`} 
                alt={`${spreadType} spread`}
                style={{ width: '2.5rem', height: '2.5rem' }}
              />
            </div>
            <div className="reading-actions">
              <button 
                className="reading-action-btn delete-btn"
                onClick={(e) => {
                  e.preventDefault(); // Prevent navigation when deleting
                  e.stopPropagation();
                  onDelete(reading._id || reading.id);
                }}
                title={t('diary.deleteReading')}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
          
          <div className="reading-date">
            {(() => {
              try {
                const dateValue = reading.timestamp || reading.createdAt || reading.readingDate || new Date();
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) {
                  // If date is invalid, use current date
                  const now = new Date();
                  return (
                    <>
                      <div className="date-day">{format(now, 'dd')}</div>
                      <div className="date-month-year">{format(now, 'MMM yyyy')}</div>
                    </>
                  );
                }
                return (
                  <>
                    <div className="date-day">{format(date, 'dd')}</div>
                    <div className="date-month-year">{format(date, 'MMM yyyy')}</div>
                  </>
                );
              } catch (error) {
                logger.error('Date formatting error:', error, 'Reading:', reading);
                const now = new Date();
                return (
                  <>
                    <div className="date-day">{format(now, 'dd')}</div>
                    <div className="date-month-year">{format(now, 'MMM yyyy')}</div>
                  </>
                );
              }
            })()}
          </div>
          
          <h3 className="reading-title">{reading.spread || reading.spreadName || t('diary.untitledReading')}</h3>

          {reading.question && (
            <div className="reading-question">
              <strong>{t('diary.question')}</strong> "{reading.question}"
            </div>
          )}

          {reading.notes && (
            <div className="reading-notes-preview">
              <small>{t('diary.notes')}</small>
              <p>{reading.notes.substring(0, 18)}{reading.notes.length > 18 && '...'}</p>
            </div>
          )}

          {reading.aiExplanation && (
            <div className="reading-ai-preview">
              <small style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#9333ea' }}>
                <span>✨</span> {t('diary.aiInsight')}
              </small>
              <p style={{ color: '#555', fontStyle: 'italic' }}>
                {reading.aiExplanation.substring(0, 30)}{reading.aiExplanation.length > 30 && '...'}
              </p>
            </div>
          )}

          <div className="cards-info">
            <span className="cards-count">
              {reading.cards?.length || 0} {t('diary.cardsDrawn')}
            </span>
          </div>
        </div>

        {/* Right Column - 40% width */}
        <div className="reading-content-right">
          <div 
            className={`tarot-cards-preview ${reading.cards?.length > 1 ? 'multi-card-collage' : 'single-card'}`}
            style={{ '--type-icon-url': `url(/images/${iconSrc})` }}
          >
            {reading.cards && reading.cards.length > 0 && reading.cards.slice(0, 3).map((cardData, index) => {
              if (!cardData) return null;
              
              const cardName = cardData.card?.name || cardData.name || 'Unknown Card';
              const isReversed = cardData.card?.position === 'reversed' || cardData.position === 'reversed';
              
              // Use the actual card image URL from the card data, or create local path from image URL
              const getCardImageSrc = (cardData) => {
                // If card has image URL, convert it to local path
                if (cardData.card?.image) {
                  const imageUrl = cardData.card.image;
                  const fileName = imageUrl.split('/').pop(); // Get filename from URL
                  return `/images/tarot-cards/${fileName}`;
                }
                
                // Fallback: try to extract card number from name and use local image
                const cardName = cardData.card?.name || cardData.name || '';
                const match = cardName.match(/^(\d+)\./); // Match number at start like "0. The Fool"
                if (match) {
                  return `/images/tarot-cards/image_${match[1]}.jpg`;
                }
                
                // Final fallback
                return '/images/tarot-cards/image_1.jpg';
              };
              
              return (
                <div 
                  key={index} 
                  className={`card-image-preview ${isReversed ? 'reversed' : ''}`}
                  style={reading.cards.length > 1 ? { 
                    '--card-index': index,
                    zIndex: reading.cards.length - index 
                  } : {}}
                >
                  <img 
                    src={getCardImageSrc(cardData)}
                    alt={cardName}
                    onError={(e) => {
                      e.target.src = '/images/tarot-cards/image_1.jpg'; // Fallback image
                    }}
                  />
                </div>
              );
            })}
            
            {reading.cards && reading.cards.length > 3 && (
              <div className="more-cards-indicator">
                +{reading.cards.length - 3} {t('diary.moreCards')}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </Link>
  );
};

export default DiaryPage;
